import { LitElement, html, render } from 'lit';
import { style } from "./baseEditor.style";
import { property } from 'lit/decorators.js';
import { msg } from '@omegagrid/localize';
import { getGridSource } from '../tableAdapter';
import { ReportResult } from '../../../model';
import { ColorTypes, dom, OmegaComponent, events, Input } from '@omegagrid/core';
import { ContextData, PlotBand, PlotBandDefaults, PlotBandLabel, ReportContext } from "@reporting/rp-types/analytics";
import { Grid, GridEditEvent, GridEvent, isFormula } from '@omegagrid/grid';
import { BaseVisualization } from '../..';
import { BaseSidebar, CodeEditor } from "@reporting/rp-app-host";
import { HighchartOptionsCodeEditorAdapter } from '../../../adapters/highchartOptionsCodeEditorAdapter';
import { ColorCellEditor, colorCellRenderer } from './colorCellEditor';
import { TextCellEditor } from './textCellEditor';

const PLOT_BANDS_ROW_HEIGHT = 22;


export class BaseVisualizationEditor<TVisualization extends BaseVisualization = BaseVisualization> extends LitElement {

	static styles = [OmegaComponent.styles, ...BaseSidebar.styles, style];

	@property({type: Object})
	reportContext: ReportContext;

	@property({type: Object})
	result: ReportResult;

	@property({type: Object})
	visualization: TVisualization;

	@property({type: Boolean})
	grid = false;

	get contextData() {
		return this.reportContext?.context;
	}

	get viewOptions() {
		if (!this.contextData) return null;
		if (!this.contextData.view_options) this.contextData.view_options = {};
		return this.contextData.view_options;
	}

	/** input the report cell references (=A1) are written into */
	protected _activeInput: Input;

	private _dataGrid: Grid;
	get dataGrid() {
		if (this._dataGrid) return this._dataGrid;
		this._dataGrid = dom.createElement<Grid>('og-grid', {
			style: { height: '100%' },
			listeners: [
				['grid.click', (e: GridEvent) => {
					if (!e.cell) return;
					this._onCellClick(e.cell.address);
				}]
			]
		});
		return this._dataGrid;
	}

	_onCellClick = (address: string) => {
		if (!this._activeInput) return;
		this._activeInput.value = `=${address}`;
		setTimeout(() => this._activeInput.focus(), 100);
		this.updateProperty(this._activeInput.dataset.key, this._activeInput.value);
	}

	loadDataGrid() {
		if (!this.result || !this.reportContext) return;
		const source = getGridSource(this.result, this.reportContext);
		this.dataGrid.setSourceData(source);
	}

	/** track the input a clicked report cell is written into */
	protected _onFocus = (e: FocusEvent) => {
		this._activeInput = e.target as Input;
	}

	/**
	 * Refresh the live preview and announce the edit. The visualization may
	 * be missing (dashboard subeditor before the item finished loading) -
	 * the edit event from the editor itself keeps the change tracked.
	 */
	protected applyChange() {
		this.visualization?.refresh(this.contextData as ContextData);
		this.dispatchEvent(new CustomEvent('edit', {bubbles: true, composed: true}));
	}

	updateProperty(key: string, value: any) {
		// dot path support, e.g. plot_bands.0.from
		const parts = key.split('.');
		let record = this.viewOptions as Record<string, any>;
		for (let i = 0; i < parts.length - 1; i++) {
			record[parts[i]] ??= isNaN(Number(parts[i + 1])) ? {} : [];
			record = record[parts[i]];
		}
		record[parts[parts.length - 1]] = value;

		this.applyChange();
	}

	/** generic change handler of the form fields, driven by the data-key/data-type attributes */
	protected _onDataChange = (e: events.ChangeEvent) => {
		const key = (e.target as HTMLElement)?.dataset?.key;
		if (!key) return;
		const type = (e.target as HTMLElement)?.dataset?.type;

		let value: unknown = e.value;
		switch (type) {
			case 'color':
				value = (value as string || '').replace(/^#+/g, '');
				break;
			case 'number':
				value = value === '' || value == null ? undefined : Number(value);
				break;
			case 'boolean':
				// an empty option clears the property (tri state fields, e.g. auto)
				value = value === '' || value == null ? undefined : value === '1';
				break;
			case 'enum':
				// a string option list where the empty option clears the property
				value = value === '' || value == null ? undefined : value;
				break;
		}

		this.updateProperty(key, value);
	}

	protected booleanItems = (yes = msg('Visible'), no = msg('Hidden')) => [
		{k: '1', v: yes, icon: 'eye'},
		{k: '0', v: no, icon: 'eye-slash'},
	];

	/**
	 * Plot bands of the view options, shared by the gauge and the chart editor.
	 * The list is addressed by a dot path - the gauge keeps them flat
	 * (plot_bands), charts nest them per axis (e.g. y_axis.plot_bands) and
	 * render the form once for each axis.
	 */
	getPlotBands(path = 'plot_bands'): PlotBand[] {
		const parts = path.split('.');
		let record = this.viewOptions as Record<string, any>;
		for (let i = 0; i < parts.length - 1; i++) {
			record = record[parts[i]] ??= {};
		}
		return record[parts[parts.length - 1]] ??= [];
	}

	/** defaults inherited by the bands of the list, the sibling key of the path */
	getPlotBandDefaults(path = 'plot_bands'): PlotBandDefaults {
		const parts = `${path}_defaults`.split('.');
		let record = this.viewOptions as Record<string, any>;
		for (let i = 0; i < parts.length - 1; i++) {
			record = record[parts[i]] ??= {};
		}
		return record[parts[parts.length - 1]] ??= {};
	}

	/**
	 * Grid instances of the plot band lists, cached per path. The cached
	 * element is embedded into the template as-is, so the editor re-renders
	 * do not recreate the grid and its state (scroll, selection) survives -
	 * edits are written into the model incrementally instead of rebuilding.
	 */
	private _plotBandsGrids = new Map<string, Grid>();

	private getPlotBandsGrid(path: string): Grid {
		let grid = this._plotBandsGrids.get(path);
		if (!grid) {
			grid = dom.createElement<Grid>('og-grid', {
				listeners: [
					['grid.edit', (e: Event) => this._onPlotBandsEdit(e as GridEditEvent, path)],
					['grid.action', (e: Event) => this._onPlotBandsAction(e as GridEvent, path)],
				]
			});
			this._plotBandsGrids.set(path, grid);
			this.loadPlotBandsGrid(grid, path);
		}
		return grid;
	}

	private updatePlotBandsGridHeight(grid: Grid, count: number) {
		// bands + the defaults row + the spare row + header
		grid.style.height = `${(count + 3) * PLOT_BANDS_ROW_HEIGHT + 40}px`;
	}

	/** cell value of a from/to/text field, see TextCellEditor for the space prefix */
	private plotBandTextValue(value: string|number) {
		return value && isFormula(value.toString()) ? (' ' + value.toString()) : (value ?? '');
	}

	/**
	 * Load the plot bands grid. The first data row holds the defaults
	 * inherited by every band without an own value (color/label only, the
	 * bounds stay per band), the last row is a spare one - editing it appends
	 * a new band. The from/to values may be report cell references (=A1): the
	 * grid would treat them as own formulas, so they are prefixed with
	 * a space in the cells (see TextCellEditor) and the space is stripped
	 * again when stored into the band list.
	 */
	private loadPlotBandsGrid(grid: Grid, path: string) {
		const bands = this.getPlotBands(path);
		const defaults = this.getPlotBandDefaults(path);

		const dropdownCell = (value: string, items: {k: string, v: string}[]) => ({
			v: value ?? '',
			dv: items.find(item => item.k === (value ?? ''))?.v,
		});

		const alignItems = [
			{k: '', v: msg('Auto')},
			{k: 'left', v: msg('Left')},
			{k: 'center', v: msg('Center')},
			{k: 'right', v: msg('Right')},
		];

		const verticalAlignItems = [
			{k: '', v: msg('Auto')},
			{k: 'top', v: msg('Top')},
			{k: 'middle', v: msg('Middle')},
			{k: 'bottom', v: msg('Bottom')},
		];

		const bandCells = (band: PlotBand|PlotBandDefaults) => [
			{},
			{v: this.plotBandTextValue((band as PlotBand).from)},
			{v: this.plotBandTextValue((band as PlotBand).to)},
			{v: band.color},
			{v: this.plotBandTextValue(band.label?.text)},
			dropdownCell(band.label?.align, alignItems),
			dropdownCell(band.label?.vertical_align, verticalAlignItems),
			{v: band.label?.rotation ?? ''},
		];

		// the cell behavior (editors, renderer, content) sits on the columns,
		// so the rows inserted later (the spare row) inherit it automatically
		grid.setSourceData({
			columns: [
				{
					t: 'a',
					e: false,
					w: 25,
					content: [{action: 'delete', icon: 'trash', color: ColorTypes.Red, title: msg('Remove')}],
				},
				{w: 50, t: 't', e: true, data: 1, editorFactory: () => new TextCellEditor(), header: [null, {label: msg('From')}]},
				{w: 200, t: 't', e: true, data: 2, editorFactory: () => new TextCellEditor(), header: [null, {label: msg('To')}]},
				// text type: an all-digit hex color would otherwise be typed as a number
				{w: 70, t: 't', e: true, data: 3, renderer: colorCellRenderer, editorFactory: () => new ColorCellEditor(), header: [null, {label: msg('Color')}]},
				{w: 150, t: 't', e: true, data: 4, editorFactory: () => new TextCellEditor(), header: [{label: msg('Label'), cs: 4}, {label: msg('Text')}]},
				{w: 80, l: 1, t: 's', e: true, data: 5, content: alignItems, header: [null, {label: msg('Align')}]},
				{w: 80, l: 1, t: 's', e: true, data: 6, content: verticalAlignItems, header: [null, {label: msg('V. align')}]},
				{w: 70, l: 1, t: 'n', e: true, data: 7, header: [null, {label: msg('Rotation')}]},
			],
			rows: [
				{data: bandCells(defaults)},
				...bands.map(band => ({data: bandCells(band)})),
				// spare row appending a new band, no delete action on it
				{data: [null, {v: ''}, {v: ''}, {v: ''}, {v: ''}, {v: ''}, {v: ''}, {v: ''}]},
			],
			cells: [
				// the defaults row: no delete action, no bounds
				{r: 0, c: 0, t: 't', v: ''},
				{r: 0, c: 1, t: 't', e: false, v: msg('All'), s: {italic: true}},
				{r: 0, c: 2, t: 't', e: false, v: ''},
				// the spare row: no delete action
				{r: bands.length + 1, c: 0, t: 't', v: ''},
			],
			options: {
				scrolling: {verticalOverscroll: false},
				rowNumbers: true,
				colLetters: false,
				columnTree: false,
				autofill: false,
				undoRedo: false,
				commandWindow: false,
				rowResize: false,
				columnResize: true,
				columnHeaderTreeButtons: true,
				emptyPlaceholder: false,
			}
		});

		this.updatePlotBandsGridHeight(grid, bands.length);
	}

	/** enable the actions of the edited spare row and append a fresh spare one */
	private promoteSpareRow(grid: Grid, index: number) {
		const actionCell = grid.model.rows[index]?.cell(0);
		if (actionCell) {
			actionCell.type = 'a';
			grid.rendering.updateCells([actionCell]);
		}

		grid.editing.insertRows({index: index + 1, after: false, count: 1, dispatchEvent: false});
		const spareCell = grid.model.rows[index + 1]?.cell(0);
		if (spareCell) {
			spareCell.type = 't';
			grid.rendering.updateCells([spareCell]);
		}
	}

	/** edits of the plot bands grid go to the band list, a spare row edit appends */
	/**
	 * Write one grid column value into a band (or the defaults). An empty
	 * value deletes the property, so the band falls back to the inherited
	 * default (an undefined value would still override it in the merge).
	 */
	private writePlotBandValue(band: PlotBand|PlotBandDefaults, column: number, value: unknown): boolean {
		const empty = value == null || value === '';
		// strip the space prefix keeping the =references out of the grid formulas
		const text = () => typeof value === 'string' ? value.replace(/^ +/, '') : value as number;
		const label = () => (band.label ??= {});

		switch (column) {
			case 1:
				if (empty) delete (band as PlotBand).from;
				else (band as PlotBand).from = text();
				break;
			case 2:
				if (empty) delete (band as PlotBand).to;
				else (band as PlotBand).to = text();
				break;
			case 3:
				if (empty) delete band.color;
				else band.color = value.toString().replace(/^#+/g, '');
				break;
			case 4:
				if (empty) delete band.label?.text;
				else label().text = text().toString();
				break;
			case 5:
				if (empty) delete band.label?.align;
				else label().align = value as PlotBandLabel['align'];
				break;
			case 6:
				if (empty) delete band.label?.vertical_align;
				else label().vertical_align = value as PlotBandLabel['vertical_align'];
				break;
			case 7:
				if (empty || isNaN(Number(value))) delete band.label?.rotation;
				else label().rotation = Number(value);
				break;
			default:
				return false;
		}
		return true;
	}

	/**
	 * Edits of the plot bands grid go to the band list - the first row edits
	 * the defaults, a spare row edit appends a new band.
	 */
	private _onPlotBandsEdit(e: GridEditEvent, path: string) {
		e.stopPropagation();
		if (e.editType !== 'value') return;
		const bands = this.getPlotBands(path);
		let changed = false;

		e.cells?.forEach((cell, i) => {
			const value = e.newValues?.[i];

			if (cell.r === 0) {
				// the defaults row has no bounds
				if (cell.c >= 3) changed = this.writePlotBandValue(this.getPlotBandDefaults(path), cell.c, value) || changed;
				return;
			}

			const index = cell.r - 1;
			if (index > bands.length) return;
			changed = this.writePlotBandValue(bands[index] ??= {}, cell.c, value) || changed;
		});

		if (changed) {
			this.applyChange();
			// a spare row edit created a band: give it the actions and a new spare
			if (bands.length + 1 >= e.grid.model.rows.length) {
				this.promoteSpareRow(e.grid, e.grid.model.rows.length - 1);
				this.updatePlotBandsGridHeight(e.grid, bands.length);
			}
		}
	}

	private _onPlotBandsAction(e: GridEvent, path: string) {
		e.stopPropagation();
		if (e.action !== 'delete' || !e.cell) return;
		const bands = this.getPlotBands(path);
		const index = e.cell.r - 1;
		if (index < 0 || index >= bands.length) return;

		bands.splice(index, 1);
		e.grid.editing.deleteRows({index: e.cell.r, count: 1, dispatchEvent: false});
		this.updatePlotBandsGridHeight(e.grid, bands.length);
		this.applyChange();
	}

	renderPlotBandsForm = (path = 'plot_bands') => html`${this.getPlotBandsGrid(path)}`;

	/**
	 * JSON editor of the raw highcharts options merged over the generated
	 * chart options (view_options.highchart_options). Cached like the grids,
	 * so the tab switches do not recreate the monaco instance.
	 */
	private _highchartOptionsEditor: CodeEditor;

	get highchartOptionsEditor(): CodeEditor {
		if (!this._highchartOptionsEditor) {
			this._highchartOptionsEditor = dom.createElement<CodeEditor>('rp-editor-code', {
				adapter: new HighchartOptionsCodeEditorAdapter(
					() => this.viewOptions as Record<string, any>,
					() => this.applyChange(),
				),
			});
		}
		return this._highchartOptionsEditor;
	}

	renderHighchartOptionsSection = () => html`
		<div style="height: 100%">${this.highchartOptionsEditor}</div>
	`;

	renderDataForm = () => html`dataform`;

	renderDataGrid = () => html`
		<og-panel style="height: 100%">
			<div slot="header">${msg('View data')}</div>
			<div slot="body" style="height: 100%">
				${this.dataGrid}
			</div>
		</og-panel>
	`;

	renderDataSection = () => html`
		<og-splitcontainer
			orientation="vertical"
			.items="${[{id: 'dataForm'}, {id: 'gridContainer'}]}"
			@splitContainer.resize="${() => this._dataGrid?.layout()}"
			.createComponent="${(id: string) => {
				const div = dom.createElement<HTMLDivElement>('div', { style: { height: '100%' } });
				if (id == 'dataForm') {
					render(this.renderDataForm(), div);
				} else if (id == 'gridContainer') {
					render(this.renderDataGrid(), div);
				}
				return div;
			}}"
			orientation="vertical"
			style="height: 100%">
		</og-splitcontainer>
	`;

	private _visualizationLoadListener: [BaseVisualization, EventListener];

	/**
	 * Adopt the report data of the visualization. Dashboard subeditors get no
	 * explicit result (the container passes it only to the standalone editor),
	 * and a reload of the visualization brings a fresh result object.
	 */
	private syncResult() {
		const data = this.visualization?.reportData;
		if (data && data !== this.result) this.result = data;
	}

	updated(props: Map<string, unknown>) {
		if (props.has('reportContext')) {
			// the cached plot band grids follow the new context data
			this._plotBandsGrids.forEach((grid, path) => this.loadPlotBandsGrid(grid, path));
			this._highchartOptionsEditor?.discard(true);
		}
		if (props.has('visualization')) {
			// forms reading from the rendered visualization (e.g. the series of
			// a chart) are re-built when it is known and every time it loads -
			// the visualization is assigned before its data arrives, and the
			// items of a derived dashboard load even later
			const previous = this._visualizationLoadListener;
			if (previous) previous[0].removeEventListener('report.load', previous[1]);
			this._visualizationLoadListener = null;

			if (this.visualization) {
				const listener = () => {
					this.syncResult();
					this.refreshEditor();
				};
				this.visualization.addEventListener('report.load', listener);
				this._visualizationLoadListener = [this.visualization, listener];
				this.syncResult();
				this.refreshEditor();
			}
		}
	}

	/** re-render the editor form (it is rendered statically into the component store container) */
	async refreshEditor() {
		this.requestUpdate();
		// if (!this.grid) {
		// 	this.requestUpdate();
		// 	return;
		// }
		// const container = await this.componentStore.get('editorContainer');
		// render(this.renderEditor(), container);
	}

}
